import React from 'react';
import PropTypes from 'prop-types';
import { cycleGroupLabel } from './cycleToolbarButtons';
import { ACTIVE_PRODUCT, INTEGRALDELTA } from '../config/productTarget';
import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';

import { activeAreaHighlights, prefillForAreaHighlight } from '../utils/areaHighlightStudy';

/**
 * WHAT IS CURRENTLY SHADED ON THE CHART — `[ Sun in Virgo (Sidereal) ] [ ⚙ ] [ ✕ ]`.
 *
 * <h2>⚠ THE PROBLEM IT SOLVES IS THAT A HEADLESS STUDY LEAVES NOTHING TO CLICK</h2>
 *
 * Before the Copilot, every shaded region arrived through a dialog, so the dialog WAS the record
 * of what had been asked: reopen it and the pickers still held the question. `study.run` paints
 * without opening anything, which is the point of it — and the cost is that a chart could carry
 * four sets of bands with no way to tell what any of them were, and no way to remove one without
 * clearing them all.
 *
 * <p>So the pill is the record. It names the question, reopens it for editing, and removes it.
 *
 * <h2>⚠ ONE PILL PER GROUP, NOT PER BAND</h2>
 *
 * A fast planet crossing a sign produces dozens of intervals and they are ONE answer.
 * `activeAreaHighlights` dedupes by `groupId` for that reason; a pill per overlay would put
 * twenty-three identical chips in the header for one question.
 *
 * <h2>⚠ THE GEAR IS HIDDEN WHEN THE SOURCE IS UNKNOWN, rather than opening an empty dialog</h2>
 *
 * Bands drawn before this existed carry no `source`, and so do the PTA/PTB cycle pillars — which
 * share the `areaHighlight` overlay type but are a different feature with a different dialog.
 * `prefillForAreaHighlight` answers null for both, and a gear that opened an unfilled form would
 * invite a reader to re-enter a question the app is standing on top of but cannot read.
 *
 * <p>The ✕ is offered either way, because removing a band needs no knowledge of what it was.
 */
export function ActiveOverlayPills({ overlays, onEdit, onRemoveGroup, product = ACTIVE_PRODUCT }) {
  /*
   * ⚠ NOTHING AT ALL ON integralDelta — the whole bar, not a filtered version of it.
   *
   * This is a REVERSAL of one commit ago, which RENAMED the chip to `PTA — Long Cycle` instead of
   * removing it. That rename was a correct fix to a different problem (the chip was calling a
   * cycle band "Area highlight", which was wrong on every product) and it is kept below for
   * astro. It was the wrong answer to the question actually asked, which was for a blank header.
   *
   * ⚠ AND THE OBJECTION THAT HELD IT BACK IS NOW ANSWERED, measured rather than taken on trust.
   * I argued against removal because the chip's ✕ looked like the only way to clear PTA/PTB
   * shading. It is not:
   *
   *   - X1 (`onDeleteLastDrawing`) removes the last addition AND purges `cycleRuns` for a cycle
   *     group, which is what makes the deletion stick.
   *   - X (`clear_all_overlays`) calls `clearOverlays()`, and as of this commit also purges
   *     `cycleRuns` — see `onClearCycleRuns`. It did not, which is the gap that verifying this
   *     turned up rather than assuming.
   *
   * So the chart's own controls own clearing, and the header owns nothing.
   */
  if (product === INTEGRALDELTA) return null;

  /*
   * On ASTRO the bar stays, and it is not cosmetic there: `study.run` paints an area highlight
   * headlessly, so the pill is the only record of what was asked and the only route to the
   * dialog that edits it. A cycle group is NAMED rather than called "Area highlight" — see
   * `cycleGroupLabel` — because that was wrong on this product too.
   */
  const active = activeAreaHighlights(overlays)
    .map((g) => ({ ...g, cycleName: cycleGroupLabel(g.groupId) }));
  if (active.length === 0) return null;

  return (
    /*
     * ⚠ ONE ROW THAT SCROLLS — `flexWrap: 'nowrap'`, where it was `'wrap'`.
     *
     * Wrapping is what turned the per-interval explosion into a screen-filling one: 34 pills
     * measured 2991px and took a second row, growing the header. At a narrower width the same run
     * would have taken five or six rows and pushed the chart down the page.
     *
     * <p>⚠ `overflowX: 'auto'` AND NOT `'hidden'`, which the brief offered. Hidden makes a pill
     * past the edge unreachable — and the pill is the only record of a headless `study.run` and
     * the only route to its ✕, so a reader with several studies open could neither read nor
     * dismiss the last of them. Scrolling bounds the height, which is the actual requirement,
     * without making a control disappear.
     *
     * <p>`minWidth: 0` is what lets the flex child shrink at all, and `maxWidth` keeps the bar
     * clear of the brand to its left and the theme/profile controls to its right rather than
     * squeezing them.
     */
    <Box
      data-testid="active-overlay-pills"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        flexWrap: 'nowrap',
        minWidth: 0,
        maxWidth: { xs: 180, sm: 320, md: 520 },
        overflowX: 'auto',
        // The scrollbar is suppressed rather than the content: a visible track inside a 64px
        // header is most of a row on its own.
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {active.map(({ groupId, label, color, count, source, cycleName }) => {
        const prefill = prefillForAreaHighlight(source);
        /*
         * ⚠ THE COUNT IS ON THE CHIP NOW — `GS.Sun in Gemini (34)` — reversing the note that used
         * to sit here, and the reason it was right then is the reason it is wrong now.
         *
         * It read: *"a chip reading 'Sun in Virgo (Sidereal) · 23' in a header competes with the
         * brand beside it, and the number is not what a reader is scanning the header for."* That
         * objection was about a header carrying MANY chips, which is what it carried — one per
         * interval. With one chip per run the crowding argument is gone, and the count becomes the
         * one thing the chip cannot otherwise say: whether the study found three intervals or
         * thirty-four. A reader who has to hover to learn that is a reader who will not.
         *
         * <p>Parenthesised rather than `· 34`, so it reads as a quantity belonging to the name
         * instead of a second field appended to it.
         *
         * <p>The tooltip keeps the long form. It is no longer the only place the figure exists, so
         * it is free to be the sentence rather than the number.
         */
        const title = count === 1 ? '1 interval shaded' : `${count} intervals shaded`;
        const name = cycleName || label || 'Area highlight';
        const chipLabel = count > 1 ? `${name} (${count})` : name;
        return (
          // `flexShrink: 0` so a pill keeps its width in the single-row container above rather
          // than being squeezed until its ✕ is unclickable.
          <Box key={groupId} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Tooltip title={title}>
              <Chip
                size="small"
                label={chipLabel}
                data-testid={`overlay-pill-${groupId}`}
                sx={{
                  // The band's OWN fill, so the pill and the shading it names are the same
                  // colour. Taking a palette colour here would make two pills over two
                  // differently-shaded regions look interchangeable.
                  bgcolor: color || 'rgba(255,255,255,0.18)',
                  color: '#fff',
                  fontWeight: 600,
                  maxWidth: 260,
                }}
              />
            </Tooltip>
            {prefill && (
              <Tooltip title="Open this highlight for editing">
                <IconButton
                  size="small"
                  aria-label={`Configure ${cycleName || label}`}
                  data-testid={`overlay-pill-edit-${groupId}`}
                  onClick={() => onEdit(prefill)}
                  sx={{ color: 'inherit', p: 0.25 }}
                >
                  <SettingsIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Remove this highlight">
              <IconButton
                size="small"
                aria-label={`Remove ${cycleName || label}`}
                data-testid={`overlay-pill-remove-${groupId}`}
                onClick={() => onRemoveGroup(groupId)}
                sx={{ color: 'inherit', p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      })}
    </Box>
  );
}

ActiveOverlayPills.propTypes = {
  /** The whole overlay list. Non-areaHighlight entries are ignored. */
  overlays: PropTypes.array,
  /** Called with the dialog prefill for a pill's gear. */
  onEdit: PropTypes.func.isRequired,
  /** The build target. Defaults to the baked one, so a caller that omits it still filters. */
  product: PropTypes.string,
  /** Called with a groupId for a pill's ✕. Must remove EVERY overlay in that group. */
  onRemoveGroup: PropTypes.func.isRequired,
};

export default ActiveOverlayPills;
